import { motion } from 'motion/react'
import type { CustomerOrderInvoice } from '../../customerOrdersApi'
import { formatDateTime } from '../../helpers/formatters'
import type { ShopLocale, ShopMessageKey } from '../../../i18n'
import { customerMotionTransition } from '../customer-area'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderInvoiceItemProps = {
  invoice: CustomerOrderInvoice
  index: number
  invoiceOpeningId: string | null
  openInvoiceHintLabel: string
  openingInvoiceHintLabel: string
  reducedMotion: boolean
  locale: ShopLocale
  t: TranslateFn
  onOpenInvoice: (invoice: CustomerOrderInvoice) => void
}

export const CustomerOrderInvoiceItem = ({
  invoice,
  index,
  invoiceOpeningId,
  openInvoiceHintLabel,
  openingInvoiceHintLabel,
  reducedMotion,
  locale,
  t,
  onOpenInvoice,
}: CustomerOrderInvoiceItemProps) => (
  <motion.li
    role="button"
    tabIndex={0}
    aria-label={`${openInvoiceHintLabel}: ${invoice.invoiceNumber}`}
    initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 5 }}
    animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
    transition={{ ...customerMotionTransition, delay: reducedMotion ? 0 : index * 0.03 }}
    whileHover={reducedMotion ? undefined : { y: -2 }}
    onClick={() => {
      if (invoiceOpeningId === invoice.id) return
      onOpenInvoice(invoice)
    }}
    onKeyDown={(event) => {
      if (invoiceOpeningId === invoice.id) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onOpenInvoice(invoice)
      }
    }}
    className="ca-surface-muted ca-theme-transition group cursor-pointer rounded-xl px-4 py-3 transition-all duration-150 hover:border-(--color-border-strong) hover:bg-(--color-bg-card) hover:shadow-(--shadow-md) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-focus-ring)_35%,transparent)]"
  >
    <article className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-(--color-status-error-border) bg-(--color-status-error-bg) text-(--color-status-error)">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8}>
            <path d="M7 3.5h7l4 4v13h-11a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
            <path d="M14 3.5v4h4M8.5 12h7M8.5 15.5h7" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-(--color-text-primary)">{invoice.invoiceNumber}</p>
          <p className="mt-1 text-xs text-(--color-text-secondary)">
            {t('customer.orderDetail.invoiceIssuedAt', {
              date: formatDateTime(invoice.invoiceDate, locale, t('common.never')),
            })}
          </p>
          <p className="mt-0.5 text-xs text-(--color-text-muted)">
            {t('customer.orderDetail.invoiceAvailableAt', {
              date: formatDateTime(invoice.uploadedAt, locale, t('common.never')),
            })}
          </p>
        </div>
      </div>

      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-(--color-text-secondary) transition group-hover:text-(--color-text-primary)">
        {invoiceOpeningId === invoice.id ? (
          <span className="inline-flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 animate-spin" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <path d="M12 3a9 9 0 1 0 9 9" />
            </svg>
            {openingInvoiceHintLabel}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            {openInvoiceHintLabel}
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        )}
      </span>
    </article>
  </motion.li>
)
